import { request } from "$/lib/api"
import storeQueries from "$/lib/queries/store-queries"
import {
  BookableServiceSlotsOverride,
  DateUtils,
  Route,
  SlotsOverrideOpeningHoursConflict,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

// Local type for form data that might have string values
type FormData = {
  date: Date | string
  slots: number | string
  note?: string | null
}

/**
 * Safely transforms form data into a valid BookableServiceSlotsOverride object
 */
function transformData(
  data: BookableServiceSlotsOverride | FormData,
): BookableServiceSlotsOverride {
  return {
    date: data.date instanceof Date ? data.date : new Date(data.date),
    slots:
      typeof data.slots === "string"
        ? Number.parseInt(data.slots, 10)
        : data.slots,
    note: data.note ?? null,
  }
}

async function fetch(storeId: string): Promise<BookableServiceSlotsOverride[]> {
  return await request<BookableServiceSlotsOverride[]>({
    url: populateRoute(
      Route.BOOKING_RULES__GET_BOOKABLE_SLOTS_OVERRIDES,
      storeId,
    ),
    method: "GET",
  })
}

async function upsert(
  storeId: string,
  data: BookableServiceSlotsOverride | FormData,
) {
  const formattedData = transformData(data)

  return await request<SlotsOverrideOpeningHoursConflict>({
    url: populateRoute(
      Route.BOOKING_RULES__UPSERT_BOOKABLE_SLOTS_OVERRIDE,
      storeId,
    ),
    method: "PATCH",
    data: formattedData,
  })
}

async function upsertMany(
  storeId: string,
  data: (BookableServiceSlotsOverride | FormData)[],
) {
  const formattedData = data.map(transformData)

  return await request<SlotsOverrideOpeningHoursConflict>({
    url: populateRoute(
      Route.BOOKING_RULES__UPSERT_BOOKABLE_SLOTS_OVERRIDES_BATCH,
      storeId,
    ),
    method: "PATCH",
    data: formattedData,
  })
}

async function remove(storeId: string, date: Date) {
  const dateObj = date instanceof Date ? date : new Date(date)

  return await request<void>({
    url: populateRoute(
      Route.BOOKING_RULES__DELETE_BOOKABLE_SLOTS_OVERRIDE,
      storeId,
      DateUtils.getDateKey(dateObj),
    ),
    method: "DELETE",
  })
}

async function removeMany(storeId: string, dates: Date[]) {
  const dateKeys = dates.map((date) =>
    date instanceof Date ? date.toISOString() : new Date(date).toISOString(),
  )

  return await request<void>({
    url: populateRoute(
      Route.BOOKING_RULES__DELETE_BOOKABLE_SLOTS_OVERRIDES_BATCH,
      storeId,
    ),
    method: "DELETE",
    data: dateKeys,
  })
}

export function useBookableServiceSlotsOverridesMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(
      bookableServiceSlotsOverridesQueries.all(storeId),
    )
  }

  // Saving an override can pull fresh opening hours from Google, which
  // rewrites the store record.
  const invalidateAfterUpsert = async () => {
    await Promise.all([
      invalidateQueries(),
      queryClient.invalidateQueries({
        queryKey: storeQueries.detail(storeId).queryKey,
      }),
    ])
  }

  const upsertMutation = useMutation({
    mutationFn: (data: BookableServiceSlotsOverride) => upsert(storeId, data),
    onSettled: invalidateAfterUpsert,
  })

  const upsertManyMutation = useMutation({
    mutationFn: (data: BookableServiceSlotsOverride[]) =>
      upsertMany(storeId, data),
    onSettled: invalidateAfterUpsert,
  })

  const deleteMutation = useMutation({
    mutationFn: (date: Date) => remove(storeId, date),
    onSettled: invalidateQueries,
  })

  const deleteManyMutation = useMutation({
    mutationFn: (dates: Date[]) => removeMany(storeId, dates),
    onSettled: invalidateQueries,
  })

  return {
    upsertMutation,
    upsertManyMutation,
    deleteMutation,
    deleteManyMutation,
  }
}

const bookableServiceSlotsOverridesQueries = {
  all: (storeId: string) =>
    queryOptions({
      queryKey: ["bookable-service-slots-overrides", storeId],
      queryFn: () => fetch(storeId),
      // Rarely changes and is invalidated on mutation; keep it out of the
      // calendar's blocking load path.
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    }),
}

export default bookableServiceSlotsOverridesQueries
